/**
 * Placement des joueurs autour de la table (ou des tables pour un défi libre).
 *
 * - Affiche les positions (Sud/Est/Nord/Ouest) avec joueur ou "Bot IA"
 * - Drag & drop pour réorganiser
 * - "Non placés" : pool partagé entre toutes les tables
 * - Toggle placement auto (géré par DraftTableEditor)
 */

import { useState, useCallback, useMemo } from 'react';
import type { DraftPlayer, SeatPosition } from '../types/draft';
import { getActiveSeats, getSeatLabel } from '../types/draft';
import { TableMasterSeatIcon } from './Icons';
import { PlayerAvatar } from './PlayerAvatar';
import { Switch } from './Switch';

function SeatMasterTip() {
  return (
    <span className="seat-master-tip" tabIndex={0}>
      <span className="seat-master-tip-trigger" aria-label="Maître de table">
        <TableMasterSeatIcon size={15} />
      </span>
      <span className="seat-master-tip-bubble" role="tooltip">
        Maître de table — le créateur sera connecté ici.
      </span>
    </span>
  );
}

// ─── Single-table component (rendered per-table) ────────────────────────

interface TableSeatsProps {
  nombreJoueurs: 3 | 4 | 5;
  compet: boolean;
  players: DraftPlayer[];
  isAdmin: boolean;
  tableIndex: number;
  tableLabel?: string;
  /** N'affiche que la localisation (Nord/Est/Sud/Ouest), pas le rôle attaquant/défenseur. */
  hideRoleLabels?: boolean;
  /** Siège maître de table (legacy : Sud — créateur connecté ici). */
  masterSeat?: SeatPosition | null;
  onDropPlayer: (pseudo: string, seat: SeatPosition, tableIndex: number) => void;
  onRemoveFromSeat: (pseudo: string) => void;
}

function TableSeats({
  nombreJoueurs,
  compet,
  players,
  isAdmin,
  tableIndex,
  tableLabel,
  hideRoleLabels = false,
  masterSeat = null,
  onDropPlayer,
  onRemoveFromSeat,
}: TableSeatsProps) {
  const [dragOverSeat, setDragOverSeat] = useState<SeatPosition | null>(null);
  const seats = getActiveSeats(nombreJoueurs, compet);

  const tablePlayers = useMemo(
    () => players.filter(p => p.tableIndex === tableIndex && p.seat),
    [players, tableIndex],
  );

  const getPlayerAtSeat = (seat: SeatPosition): DraftPlayer | null =>
    tablePlayers.find(p => p.seat === seat) || null;

  const handleDragStart = useCallback((pseudo: string, e: React.DragEvent) => {
    if (!isAdmin) return;
    e.dataTransfer.setData('text/plain', pseudo);
    e.dataTransfer.effectAllowed = 'move';
  }, [isAdmin]);

  const handleDragOver = useCallback((e: React.DragEvent, seat: SeatPosition) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSeat(seat);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverSeat(null);
  }, []);

  const handleDrop = useCallback((seat: SeatPosition, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverSeat(null);
    if (!isAdmin) return;
    const pseudo = e.dataTransfer.getData('text/plain');
    if (!pseudo) return;
    onDropPlayer(pseudo, seat, tableIndex);
  }, [isAdmin, tableIndex, onDropPlayer]);

  return (
    <div className="seat-list">
      {tableLabel && <div className="seat-list-label">{tableLabel}</div>}

      <div className="seat-list-positions">
        {seats.map(seat => {
          const player = getPlayerAtSeat(seat);
          const label = getSeatLabel(seat, compet, hideRoleLabels);

          return (
            <div
              key={seat}
              className={`seat-slot ${player ? 'occupied' : 'empty'} ${dragOverSeat === seat ? 'drop-target' : ''}`}
              onDragOver={(e) => handleDragOver(e, seat)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(seat, e)}
            >
              <span className="seat-position-label-row">
                <span className="seat-position-label">{label}</span>
                {masterSeat === seat && <SeatMasterTip />}
              </span>
              <div className="seat-player-area">
                {player ? (
                  <div
                    className={`seat-player ${player.status}`}
                    draggable={isAdmin}
                    onDragStart={(e) => handleDragStart(player.pseudo, e)}
                  >
                    <PlayerAvatar pseudo={player.pseudo} size={24} />
                    <span className="seat-player-name">{player.pseudoDisplay}</span>
                    <span className={`seat-player-status status-${player.status}`}>
                      {player.status === 'joined' ? '' :
                       player.status === 'invited' ? '(invité)' :
                       player.status === 'declined' ? '(a refusé)' :
                       player.status === 'waiting_game' ? '(en jeu)' :
                       '(hors ligne)'}
                    </span>
                    {isAdmin && (
                      <button
                        className="seat-remove-btn"
                        onClick={() => onRemoveFromSeat(player.pseudo)}
                        title="Retirer de cette position"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="seat-bot">
                    <span className="seat-bot-icon">🤖</span>
                    <span className="seat-bot-label">Bot IA</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main exported component ─────────────────────────────────────────────

export interface PlayerSeatListProps {
  nombreJoueurs: 3 | 4 | 5;
  compet: boolean;
  players: DraftPlayer[];
  isAdmin: boolean;
  tableIndex?: number;
  tableLabel?: string;
  /** N'affiche que la localisation (Nord/Est/Sud/Ouest), pas le rôle attaquant/défenseur. */
  hideRoleLabels?: boolean;
  /** Full seat list for ALL tables – the callback receives the complete list */
  onUpdateSeats: (seats: Array<{ playerPseudo: string; seat: SeatPosition; tableIndex?: number }>) => void;
  onKickPlayer?: (pseudo: string) => void;
  /** For dual-table mode: number of tables */
  nbTables?: number;
  /** Placement automatique activé (toggle admin) */
  autoPlacementEnabled?: boolean;
  onAutoPlacementChange?: (enabled: boolean) => void;
  /** Siège maître de table (legacy table libre : Sud). */
  masterSeat?: SeatPosition | null;
}

export default function PlayerSeatList({
  nombreJoueurs,
  compet,
  players,
  isAdmin,
  tableIndex = 0,
  tableLabel,
  hideRoleLabels = false,
  onUpdateSeats,
  onKickPlayer,
  nbTables = 1,
  autoPlacementEnabled = true,
  onAutoPlacementChange,
  masterSeat = null,
}: PlayerSeatListProps) {
  const isDualTable = nbTables === 2;

  const allPlacedSeats = useMemo(() => {
    const result: Array<{ playerPseudo: string; seat: SeatPosition; tableIndex: number }> = [];
    for (const p of players) {
      if (p.seat && p.tableIndex !== undefined) {
        result.push({ playerPseudo: p.pseudo, seat: p.seat, tableIndex: p.tableIndex });
      }
    }
    return result;
  }, [players]);

  const unassignedPlayers = useMemo(
    () => players.filter(p => !p.seat && p.status === 'joined'),
    [players],
  );

  const handleDropPlayer = useCallback((pseudo: string, seat: SeatPosition, targetTable: number) => {
    if (!isAdmin) return;

    const draggedEntry = allPlacedSeats.find(s => s.playerPseudo === pseudo);
    const occupantEntry = allPlacedSeats.find(s => s.seat === seat && s.tableIndex === targetTable);

    const newSeats = allPlacedSeats
      .filter(s => s.playerPseudo !== pseudo && !(s.seat === seat && s.tableIndex === targetTable))
      .map(s => ({ ...s }));

    // Place the dragged player at the target seat
    newSeats.push({ playerPseudo: pseudo, seat, tableIndex: targetTable });

    // If there was an occupant AND the dragged player came from another seat → swap
    if (occupantEntry && draggedEntry) {
      newSeats.push({
        playerPseudo: occupantEntry.playerPseudo,
        seat: draggedEntry.seat,
        tableIndex: draggedEntry.tableIndex,
      });
    }
    // If occupant exists but dragged was unassigned → occupant becomes unassigned (no entry)

    onUpdateSeats(newSeats);
  }, [isAdmin, allPlacedSeats, onUpdateSeats]);

  const handleRemoveFromSeat = useCallback((pseudo: string) => {
    if (!isAdmin) return;
    const newSeats = allPlacedSeats.filter(s => s.playerPseudo !== pseudo);
    onUpdateSeats(newSeats);
  }, [isAdmin, allPlacedSeats, onUpdateSeats]);

  const autoPlacementToggle = isAdmin && onAutoPlacementChange ? (
    <label className="seat-auto-placement-toggle" title="Placer automatiquement chaque joueur qui rejoint">
      <span>Placement auto</span>
      <Switch
        compact
        checked={autoPlacementEnabled}
        onChange={onAutoPlacementChange}
      />
    </label>
  ) : null;

  // ─── Dual-table layout ───
  if (isDualTable) {
    return (
      <div className="seat-dual-container">
        <div className="seat-dual-tables">
          <TableSeats
            nombreJoueurs={nombreJoueurs}
            compet={compet}
            players={players}
            isAdmin={isAdmin}
            tableIndex={0}
            tableLabel="Table 1"
            hideRoleLabels={hideRoleLabels}
            masterSeat={masterSeat}
            onDropPlayer={handleDropPlayer}
            onRemoveFromSeat={handleRemoveFromSeat}
          />
          <TableSeats
            nombreJoueurs={nombreJoueurs}
            compet={compet}
            players={players}
            isAdmin={isAdmin}
            tableIndex={1}
            tableLabel="Table 2"
            hideRoleLabels={hideRoleLabels}
            onDropPlayer={handleDropPlayer}
            onRemoveFromSeat={handleRemoveFromSeat}
          />
        </div>

        {/* Shared unassigned pool */}
        {unassignedPlayers.length > 0 && (
          <div className="seat-unassigned">
            <div className="seat-unassigned-label">
              Spectateurs — non placés ({unassignedPlayers.length})
            </div>
            <div className="seat-unassigned-players">
              {unassignedPlayers.map(player => (
                <div
                  key={player.pseudo}
                  className="seat-unassigned-player"
                  draggable={isAdmin}
                  onDragStart={(e) => {
                    if (!isAdmin) return;
                    e.dataTransfer.setData('text/plain', player.pseudo);
                  }}
                >
                  <PlayerAvatar pseudo={player.pseudo} size={22} />
                  <span>{player.pseudoDisplay}</span>
                  <span className="player-seat-spectator-badge">Spectateur</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {autoPlacementToggle && (
          <div className="seat-actions">
            {autoPlacementToggle}
          </div>
        )}
      </div>
    );
  }

  // ─── Single-table layout ───
  return (
    <div className="seat-single-container">
      <TableSeats
        nombreJoueurs={nombreJoueurs}
        compet={compet}
        players={players}
        isAdmin={isAdmin}
        tableIndex={tableIndex}
        tableLabel={tableLabel}
        hideRoleLabels={hideRoleLabels}
        masterSeat={masterSeat}
        onDropPlayer={handleDropPlayer}
        onRemoveFromSeat={handleRemoveFromSeat}
      />

      {unassignedPlayers.length > 0 && (
        <div className="seat-unassigned">
          <div className="seat-unassigned-label">
              Spectateurs — non placés ({unassignedPlayers.length})
          </div>
          <div className="seat-unassigned-players">
            {unassignedPlayers.map(player => (
              <div
                key={player.pseudo}
                className="seat-unassigned-player"
                draggable={isAdmin}
                onDragStart={(e) => {
                  if (!isAdmin) return;
                  e.dataTransfer.setData('text/plain', player.pseudo);
                }}
              >
                <PlayerAvatar pseudo={player.pseudo} size={22} />
                <span>{player.pseudoDisplay}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {autoPlacementToggle && (
        <div className="seat-actions">
          {autoPlacementToggle}
          {onKickPlayer && players.filter(p => p.status === 'joined').length > 1 && (
            <select
              className="seat-kick-select"
              onChange={(e) => {
                if (e.target.value) {
                  onKickPlayer(e.target.value);
                  e.target.value = '';
                }
              }}
              defaultValue=""
            >
              <option value="" disabled>Retirer un joueur...</option>
              {players
                .filter(p => p.status === 'joined')
                .map(p => (
                  <option key={p.pseudo} value={p.pseudo}>
                    {p.pseudoDisplay}
                  </option>
                ))}
            </select>
          )}
        </div>
      )}
    </div>
  );
}
